<template>
    <section class="mc-releases" aria-labelledby="release-heading" aria-roledescription="carrousel">
        <h2 id="release-heading">Nos dernières sorties</h2>
        <div class="mc-carousel-shell" @mouseenter="pauseAuto" @mouseleave="startAuto" @focusin="pauseAuto" @focusout="resumeAfterFocus">
            <button type="button" class="mc-arrow mc-arrow-prev" aria-label="Sorties précédentes" aria-controls="release-track" :disabled="atStart && atEnd" @click="move(-1)"><span class="fas fa-chevron-left" aria-hidden="true"></span></button>
            <div ref="track" id="release-track" class="mc-release-track" tabindex="0" aria-label="Dernières sorties, défilement horizontal" @scroll.passive="sync" @keydown.left.prevent="move(-1)" @keydown.right.prevent="move(1)" @keydown.home.prevent="edge(false)" @keydown.end.prevent="edge(true)">
                <article v-for="(comic, index) in releases" :key="comic.url" class="mc-release-card" :class="{ 'mc-hidden': comic.hidden }">
                    <router-link :to="comic.last_chapter.url" class="mc-release-cover" :aria-label="comic.title + ' — ' + comic.last_chapter.full_title">
                        <img :src="comic.thumbnail" :srcset="comic.thumbnail_srcset || null" sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 991px) 45vw, 20vw" :alt="comic.title" :loading="index < 5 ? 'eager' : 'lazy'" decoding="async" @error="imageFallback($event, comic)">
                        <span v-if="newChapterCount(comic)" class="mc-new-badge" title="Chapitres publiés depuis moins de 4 jours">{{ newChapterCount(comic) === 1 ? 'Nouveau Chapitre' : newChapterCount(comic) + ' Nouveaux Chapitres' }}</span>
                    </router-link>
                    <div class="mc-release-info">
                        <h3><router-link :to="comic.url" :title="comic.title">{{ comic.title }}</router-link></h3>
                        <router-link :to="comic.last_chapter.url" class="mc-release-subtitle" :title="comic.last_chapter.title || comic.last_chapter.full_title">{{ comic.last_chapter.title || '\u00a0' }}</router-link>
                        <div class="mc-release-meta">
                            <router-link :to="comic.last_chapter.url" class="mc-chapter-link" :title="comic.last_chapter.full_title">{{ chapterLabel(comic.last_chapter) }}</router-link>
                            <time class="mc-release-time" :datetime="comic.last_chapter.published_on" :title="new Date(comic.last_chapter.published_on).toLocaleString('fr-FR')"><span class="fas fa-clock" aria-hidden="true"></span>{{ publicationTime(comic.last_chapter.published_on) }}</time>
                        </div>
                    </div>
                </article>
            </div>
            <button type="button" class="mc-arrow mc-arrow-next" aria-label="Sorties suivantes" aria-controls="release-track" :disabled="atStart && atEnd" @click="move(1)"><span class="fas fa-chevron-right" aria-hidden="true"></span></button>
        </div>
        <span class="visually-hidden" aria-live="polite">Première œuvre affichée : {{ position }} sur {{ releases.length }}</span>
    </section>
</template>

<script>
export default {
    name: 'ReleaseCarousel',
    props: { releases: { type: Array, default: () => [] } },
    data() { return { atStart: true, atEnd: true, position: 0, observer: null, autoTimer: null, now: Date.now(), badgeTimer: null }; },
    mounted() {
        this.observer = new ResizeObserver(this.sync);
        this.observer.observe(this.$refs.track);
        this.sync();
        document.addEventListener('visibilitychange', this.handleVisibility);
        this.startAuto();
        this.badgeTimer = window.setInterval(() => { this.now = Date.now(); }, 1000);
    },
    beforeUnmount() {
        this.observer?.disconnect();
        this.pauseAuto();
        window.clearInterval(this.badgeTimer);
        document.removeEventListener('visibilitychange', this.handleVisibility);
    },
    watch: { releases() { this.$nextTick(() => { this.edge(false); this.sync(); this.startAuto(); }); } },
    methods: {
        startAuto() {
            this.pauseAuto();
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.releases.length < 2 || document.hidden) return;
            this.autoTimer = window.setInterval(() => this.move(1), 5000);
        },
        pauseAuto() {
            if (this.autoTimer !== null) {
                window.clearInterval(this.autoTimer);
                this.autoTimer = null;
            }
        },
        resumeAfterFocus(event) {
            if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) this.startAuto();
        },
        handleVisibility() {
            document.hidden ? this.pauseAuto() : this.startAuto();
        },
        isNew(date) {
            const age = this.now - new Date(date).getTime();
            return age >= 0 && age < 4 * 86400000;
        },
        newChapterCount(comic) {
            const dates = Array.isArray(comic.recent_chapter_dates) ? comic.recent_chapter_dates : [comic.last_chapter?.published_on];
            return dates.filter(date => this.isNew(date)).length;
        },
        chapterLabel(chapter) {
            if (chapter.chapter != null) {
                return 'Chapitre ' + chapter.chapter + (chapter.subchapter != null ? '.' + chapter.subchapter : '');
            }
            return chapter.volume != null ? 'Volume ' + chapter.volume : 'One-shot';
        },
        publicationTime(date) {
            const time = new Date(date).getTime();
            if (!Number.isFinite(time)) return '';
            const age = Date.now() - time;
            if (age < 0) return new Date(time).toLocaleDateString('fr-FR');
            if (age < 60000) return 'à l’instant';
            if (age < 3600000) return Math.floor(age / 60000) + ' min';
            if (age < 86400000) return Math.floor(age / 3600000) + ' h';
            if (age < 604800000) return Math.floor(age / 86400000) + ' j';
            return new Date(time).toLocaleDateString('fr-FR');
        },
        sync() {
            const track = this.$refs.track;
            if (!track) return;
            const card = track.firstElementChild;
            const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap) : 1;
            this.atStart = track.scrollLeft <= 2;
            this.atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
            this.position = this.releases.length ? Math.min(this.releases.length, Math.round(track.scrollLeft / step) + 1) : 0;
        },
        move(direction) {
            const track = this.$refs.track;
            const card = track.firstElementChild;
            if (!card) return;
            const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
            const visible = Math.max(1, Math.round((track.clientWidth + parseFloat(getComputedStyle(track).columnGap)) / step));
            const maxScroll = track.scrollWidth - track.clientWidth;
            const left = direction > 0
                ? (this.atEnd ? 0 : Math.min(maxScroll, track.scrollLeft + step * visible))
                : (this.atStart ? maxScroll : Math.max(0, track.scrollLeft - step * visible));
            track.scrollTo({ left, behavior: this.motion() });
        },
        edge(end) { this.$refs.track?.scrollTo({ left: end ? this.$refs.track.scrollWidth : 0, behavior: 'auto' }); },
        motion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; },
        imageFallback(event, comic) {
            const img = event.target;
            if (img.hasAttribute('srcset')) { img.removeAttribute('srcset'); img.src = comic.thumbnail; return; }
            if (!img.dataset.fallback && comic.thumbnail_small) { img.dataset.fallback = 'true'; img.src = comic.thumbnail_small; }
        }
    }
};
</script>
